import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import type {
  EditableTableColumn,
  EditableTableFilter,
  EditableTableSummary,
} from '@/components/customComponents/scalrTables'
import { EditableTable } from '@/components/customComponents/scalrTables'
import { Checkbox } from '@/components/ui/checkbox'
import type { FeatureFlags } from '@/config/types'
import { mockEmployees } from '@/mocks/employeeData'

// Define column names for permissions
const PERMISSION_COLUMNS: Array<{ key: keyof FeatureFlags; name: string }> = [
  { key: 'dashboard', name: 'Dashboard' },
  { key: 'claritySales', name: 'Clarity Sales' },
  { key: 'clarityLabor', name: 'Clarity Labor' },
  { key: 'claritySpend', name: 'Clarity Spend' },
  { key: 'claritySpeedOfService', name: 'Clarity Speed Of Service' },
  { key: 'clarityLoyalty', name: 'Clarity Loyalty' },
  { key: 'clarityProfitAndLoss', name: 'Clarity Profit And Loss' },
  { key: 'clarityLensSales', name: 'Clarity Lens Sales' },
  { key: 'clarityLensSpend', name: 'Clarity Lens Spend' },
  { key: 'clarityLensLabor', name: 'Clarity Lens Labor' },
  { key: 'clarityLensProfitAndLoss', name: 'Clarity Lens Profit And Loss' },
  { key: 'targetsKpiPayout', name: 'Targets Kpi Payout' },
  { key: 'targetsGoalPlans', name: 'Targets Goal Plans' },
  { key: 'scalrBaseProductMix', name: 'Scalr Base Product Mix' },
  { key: 'scalrBaseIngredients', name: 'Scalr Base Ingredients' },
  { key: 'scalrBaseMasterLabor', name: 'Scalr Base Master Labor' },
  { key: 'coreSuiteBankLines', name: 'Core Suite Bank Lines' },
]

// Permission row schema - one row per employee with all page permissions
const permissionSchema = z.object({
  id: z.string(),
  employeeName: z.string(),
  employeeId: z.string(),
  permissions: z.record(z.string(), z.boolean()), // page|feature as key, boolean as value
})

const formSchema = z.object({
  permissions: z.array(permissionSchema),
})

type FormData = z.infer<typeof formSchema>
type PermissionRow = z.infer<typeof permissionSchema>

// Generate initial permissions data - one row per employee
const generateInitialPermissions = (): PermissionRow[] => {
  const permissions: PermissionRow[] = []
  const employees = mockEmployees.slice(0, 15) // Use first 15 employees

  employees.forEach((employee) => {
    const allPermissions: Record<string, boolean> = {}

    // For each feature, add its permission
    PERMISSION_COLUMNS.forEach(({ key }) => {
      // Use feature key directly as permission key
      allPermissions[key] = Math.random() > 0.5
    })

    permissions.push({
      id: employee.id,
      employeeName: employee.name,
      employeeId: employee.id,
      permissions: allPermissions,
    })
  })

  return permissions
}

const PermissionsTab = () => {
  const { t } = useTranslation()
  const [isEditMode, setIsEditMode] = useState(false)
  const [editedRows, setEditedRows] = useState<Set<string>>(new Set())
  const [newRows, setNewRows] = useState<Set<string>>(new Set())
  const [deletedRows, setDeletedRows] = useState<Set<string>>(new Set())
  const [permissions, setPermissions] = useState<PermissionRow[]>(() =>
    generateInitialPermissions()
  )

  // Filter
  const [searchText, setSearchText] = useState('')

  // Form setup
  const formMethods = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      permissions,
    },
  })

  const { watch, reset, setValue } = formMethods

  // Update form when permissions change
  useEffect(() => {
    reset({ permissions })
  }, [permissions, reset])

  // Watch for changes
  const watchedPermissions = watch('permissions')

  // Track edited rows
  useEffect(() => {
    if (!isEditMode) {
      setEditedRows(new Set())
      return
    }

    const edited = new Set<string>()
    watchedPermissions.forEach((permission, index) => {
      const original = permissions[index]

      if (!original) return

      // Check if any permission has changed
      const hasChanged = Object.keys(permission.permissions).some((key) => {
        return permission.permissions[key] !== original.permissions[key]
      })

      if (hasChanged) {
        edited.add(permission.id)
      }
    })

    setEditedRows(edited)
  }, [watchedPermissions, permissions, isEditMode])

  // Define table columns
  const columns: EditableTableColumn<PermissionRow>[] = useMemo(() => {
    const cols: EditableTableColumn<PermissionRow>[] = [
      {
        header: 'Employee Name',
        accessorKey: 'employeeName',
        minWidth: '200px',
        sticky: 'left',
        renderCell: ({ value }) => <span className="font-medium">{value}</span>,
      },
    ]

    // Add dynamic permission columns
    PERMISSION_COLUMNS.forEach(({ key, name }) => {
      cols.push({
        header: name,
        accessorKey: `permissions.${key}` as any,
        minWidth: '150px',
        renderCell: ({ row }) => {
          const hasPermission = row.permissions[key] || false
          return (
            <div className="flex">
              {hasPermission ? (
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                  Enabled
                </span>
              ) : (
                <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                  Disabled
                </span>
              )}
            </div>
          )
        },
        renderEditCell: ({ row, rowIndex }) => {
          const hasPermission = row.permissions[key] || false

          return (
            <div className="flex items-center">
              <Checkbox
                checked={hasPermission || false}
                onCheckedChange={(checked) => {
                  const newPermissions = [...watchedPermissions]
                  newPermissions[rowIndex] = {
                    ...newPermissions[rowIndex],
                    permissions: {
                      ...newPermissions[rowIndex].permissions,
                      [key]: checked as boolean,
                    },
                  }
                  setValue('permissions', newPermissions)
                }}
              />
            </div>
          )
        },
      })
    })

    return cols
  }, [watchedPermissions, setValue])

  // Define filters
  const filters: EditableTableFilter[] = useMemo(
    () => [
      {
        type: 'search',
        placeholder: 'Search by employee name...',
        value: searchText,
        onChange: setSearchText,
      },
    ],
    [searchText]
  )

  // Define summary items
  const summaryItems: EditableTableSummary[] = useMemo(
    () => [
      {
        label: 'Total Permissions',
        value: permissions.length,
      },
      {
        label: 'Unique Employees',
        value: new Set(permissions.map((p) => p.employeeName)).size,
        className: 'text-green-600',
      },
    ],
    [permissions]
  )

  const handleEdit = useCallback(() => {
    setIsEditMode(true)
    setEditedRows(new Set())
    setNewRows(new Set())
    setDeletedRows(new Set())
  }, [])

  const handleCancel = useCallback(() => {
    setIsEditMode(false)
    setEditedRows(new Set())
    setNewRows(new Set())
    setDeletedRows(new Set())
    reset({ permissions })
  }, [permissions, reset])

  const handleSave = useCallback(
    (data: {
      added: PermissionRow[]
      edited: PermissionRow[]
      deleted: PermissionRow[]
      all: PermissionRow[]
    }) => {
      // Update the permissions state
      setPermissions(data.all)
      setIsEditMode(false)
      setEditedRows(new Set())
      setNewRows(new Set())
      setDeletedRows(new Set())

      console.log('=== Permission Changes ===')
      console.log('Added:', data.added.length, 'rows')
      console.log('Added rows:', data.added)
      console.log('Edited:', data.edited.length, 'rows')
      console.log('Edited rows:', data.edited)
      console.log('Deleted:', data.deleted.length, 'rows')
      console.log('Deleted rows:', data.deleted)
      console.log('Updated permissions:', data.all)
      console.log('=========================')
    },
    []
  )

  const handleSubmit = useCallback(
    (formData: any) => {
      const dataArray = formData.permissions || []

      const addedData = dataArray.filter(
        (row: PermissionRow) => newRows.has(row.id) && !deletedRows.has(row.id)
      )

      const editedData = dataArray.filter(
        (row: PermissionRow) =>
          editedRows.has(row.id) &&
          !newRows.has(row.id) &&
          !deletedRows.has(row.id)
      )

      const deletedData = dataArray.filter((row: PermissionRow) =>
        deletedRows.has(row.id)
      )

      const allData = dataArray.filter(
        (row: PermissionRow) => !deletedRows.has(row.id)
      )

      handleSave({
        added: addedData,
        edited: editedData,
        deleted: deletedData,
        all: allData,
      })
    },
    [editedRows, newRows, deletedRows, handleSave]
  )

  return (
    <EditableTable<PermissionRow>
      title="User Permissions"
      description="Manage user permissions for different pages and features"
      formMethods={formMethods}
      formFieldName="permissions"
      columns={columns}
      filters={filters}
      summaryItems={summaryItems}
      itemsPerPage={6}
      tableHeight={600}
      isEditMode={isEditMode}
      editedRows={editedRows}
      newRows={newRows}
      deletedRows={deletedRows}
      setEditedRows={setEditedRows}
      setNewRows={setNewRows}
      setDeletedRows={setDeletedRows}
      onEdit={handleEdit}
      onCancel={handleCancel}
      onSave={handleSave}
      onSubmit={handleSubmit}
      showStatusIndicator={true}
      statusIndicatorWidth="10px"
      emptyMessage="No permissions found"
    />
  )
}

export default PermissionsTab
